import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';
import type {
  BulkCreateServiceTicketRequest,
  BulkCreateServiceTicketResponse,
  ServiceTicket,
  ServiceTicketTask,
  TaskType,
  UpsertServiceTicketRequest,
  WorkOrder,
} from '@/types/serviceTicket';
import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

type BlobPart = Blob | ArrayBuffer;

/**
 * RTK Query API for Service Tickets.
 */
export const serviceTicketsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getServiceTickets: build.query<ApiResponse<ServiceTicket[]>, ApiListingRequest>({
      query: (data) =>
        athena({
          url: 'v2/Operations/service-tickets',
          method: 'POST',
          data,
        }),
      providesTags: ['ServiceTickets'],
    }),
    getServiceTicket: build.query<ApiResponse<ServiceTicket>, string>({
      query: (id) =>
        athena({
          url: `v2/Operations/service-tickets/${id}`,
          method: 'GET',
        }),
      transformResponse: (response: ApiResponse<ServiceTicket>) => {
        if (response?.data?.notes) {
          response.data.notes = [...response.data.notes].sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA; // Newest first
          });
        }
        return response;
      },
      providesTags: (_result, _error, id) => [{ type: 'ServiceTickets', id }],
    }),
    // GET athena/v2/Operations/service-tickets/{id}/work-order
    getServiceTicketWorkOrder: build.query<WorkOrder, string>({
      queryFn: async (id, _queryApi, _extraOptions, fetchWithBQ) => {
        const response = await fetchWithBQ(
          athena({
            url: `v2/Operations/service-tickets/${id}/work-order`,
            method: 'GET',
            responseType: 'blob',
          }),
        );
        const data = response.data as Blob;
        return { data };
      },
    }),
    getServiceTicketTaskTypes: build.query<ApiResponse<TaskType[]>, ApiListingRequest | void>({
      query: (data) =>
        athena({
          url: 'v2/operations/service-tickets/task-types',
          method: 'POST',
          data: data || {
            pagination: {
              current_page: 1,
              items_per_page: 1000,
              sort_by: 'name',
              sort_ascending: true,
            },
          },
        }),
      providesTags: ['TaskTypes'],
    }),
    upsertServiceTicket: build.mutation<ApiResponse<ServiceTicket>, UpsertServiceTicketRequest>({
      query: ({ id, ...data }) =>
        boro({
          url: id ? `v2/Operations/service-tickets/${id}` : 'v2/Operations/service-tickets',
          method: id ? 'PATCH' : 'POST',
          data,
        }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Invalidate tags after successful mutation
          const tags: Array<'ServiceTickets' | { type: 'ServiceTickets'; id: string }> = [
            'ServiceTickets',
          ];
          if (arg.id) {
            tags.push({ type: 'ServiceTickets', id: String(arg.id) });
          }
          dispatch(api.util.invalidateTags(tags));
        } catch {
          // Error handled in component
        }
      },
      invalidatesTags: (_, __, { id }) => {
        const tags: Array<'ServiceTickets' | { type: 'ServiceTickets'; id: string }> = [
          'ServiceTickets',
        ];
        if (id) {
          tags.push({ type: 'ServiceTickets', id: String(id) });
        }
        return tags;
      },
    }),
    bulkCreateServiceTickets: build.mutation<
      ApiResponse<BulkCreateServiceTicketResponse>,
      BulkCreateServiceTicketRequest
    >({
      query: (data) =>
        boro({
          url: 'v2/Operations/service-tickets/bulk',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['ServiceTickets'],
    }),
    exportServiceTickets: build.mutation<BlobPart, { kind: string; data: ApiListingRequest }>({
      query: ({ kind, data }) => {
        return athena({
          url: `v2/Operations/service-tickets/${kind}`,
          method: 'POST',
          data,
          responseType: 'blob',
        });
      },
    }),
    createServiceTicketTask: build.mutation<
      ApiResponse<ServiceTicketTask>,
      {
        serviceTicketId: string;
        task_type_id: string;
        description?: string;
      }
    >({
      query: ({ serviceTicketId, ...data }) =>
        boro({
          url: `v2/Operations/service-tickets/${serviceTicketId}/tasks`,
          method: 'POST',
          data,
        }),
      invalidatesTags: (_, __, { serviceTicketId }) => [
        'ServiceTickets',
        { type: 'ServiceTickets', id: serviceTicketId },
      ],
    }),
    resolveServiceTicketTask: build.mutation<
      ApiResponse<ServiceTicketTask>,
      {
        serviceTicketId: string;
        taskId: string;
        resolution: 'pending' | 'success' | 'failure';
        resolution_reason_id?: string;
        resolution_notes?: string;
      }
    >({
      query: ({ serviceTicketId, taskId, ...data }) =>
        boro({
          url: `v2/Operations/service-tickets/${serviceTicketId}/tasks/${taskId}/resolve`,
          method: 'POST',
          data,
        }),
      invalidatesTags: (_, __, { serviceTicketId }) => [
        'ServiceTickets',
        { type: 'ServiceTickets', id: serviceTicketId },
      ],
    }),
    updateServiceTicketTask: build.mutation<
      ApiResponse<ServiceTicketTask>,
      {
        serviceTicketId: string;
        taskId: string;
        task_type_id?: string;
        description?: string;
        sort_order?: number;
      }
    >({
      query: ({ serviceTicketId, taskId, ...data }) =>
        boro({
          url: `v2/Operations/service-tickets/${serviceTicketId}/tasks/${taskId}`,
          method: 'PATCH',
          data,
        }),
      invalidatesTags: (_, __, { serviceTicketId }) => [
        'ServiceTickets',
        { type: 'ServiceTickets', id: serviceTicketId },
      ],
    }),
    deleteServiceTicketTask: build.mutation<void, { serviceTicketId: string; taskId: string }>({
      query: ({ serviceTicketId, taskId }) =>
        boro({
          url: `v2/Operations/service-tickets/${serviceTicketId}/tasks/${taskId}`,
          method: 'DELETE',
        }),
      invalidatesTags: (_, __, { serviceTicketId }) => [
        'ServiceTickets',
        { type: 'ServiceTickets', id: serviceTicketId },
      ],
    }),
  }),
});

/**
 * RTK Query API for Service Ticket Resources.
 */
export const serviceTicketResourcesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getServiceTicketResources: build.query<
      ApiResponse<{
        statuses: string[];
        priorities: string[];
        task_types: string[];
      }>,
      void
    >({
      query: () =>
        athena({
          url: 'v2/Resources/service-tickets',
          method: 'GET',
        }),
    }),
  }),
});

export const {
  useGetServiceTicketsQuery,
  useGetServiceTicketQuery,
  useLazyGetServiceTicketWorkOrderQuery,
  useGetServiceTicketTaskTypesQuery,
} = serviceTicketsApi;

export const {
  useUpsertServiceTicketMutation,
  useBulkCreateServiceTicketsMutation,
  useExportServiceTicketsMutation,
  useCreateServiceTicketTaskMutation,
  useResolveServiceTicketTaskMutation,
  useUpdateServiceTicketTaskMutation,
  useDeleteServiceTicketTaskMutation,
} = serviceTicketsApi;

export const { useGetServiceTicketResourcesQuery } = serviceTicketResourcesApi;

export const {
  endpoints: { getServiceTickets, getServiceTicket, upsertServiceTicket },
} = serviceTicketsApi;
